import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { api, fmtNumber } from "@/lib/api";
import { TID } from "@/constants/tids";
import { logError } from "@/lib/logger";
import { rangeParams, RANGE_OPTIONS } from "@/lib/dateRanges";
import { useNodeExport } from "@/hooks/useNodeExport";
import TabReportPrint from "@/components/shared/TabReportPrint";
import TabLoadError from "@/components/shared/TabLoadError";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Filter, Loader2, Sparkles, CalendarRange } from "lucide-react";
import { toast } from "sonner";

const REMARK_PLUGINS = [remarkGfm];

export default function Funnel({ range, onRangeChange, onRegisterExport }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedPipe, setSelectedPipe] = useState(null);
  const printRef = useRef(null);
  const rangeLabel = RANGE_OPTIONS.find((o) => o.value === range)?.label || "All Time";

  const { exportPdf, printNode } = useNodeExport(
    printRef,
    () => !!(data && data.pipelines && data.pipelines.length),
    "salestap-funnel"
  );

  useEffect(() => {
    if (!onRegisterExport) return;
    onRegisterExport("funnel", { exportPdf, printFn: printNode });
  }, [onRegisterExport, exportPdf, printNode, data]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { start, end } = rangeParams(range);
      const [f, rep] = await Promise.all([
        api.getFunnel(start, end),
        api.getFunnelReport().catch(() => ({ report_md: null })),
      ]);
      setData(f);
      setSelectedPipe(f?.primary_pipeline_id || (f?.pipelines?.[0]?.pipeline_id ?? null));
      setReport(rep?.report_md || null);
    } catch (e) {
      logError("Failed to load funnel:", e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    setReportLoading(true);
    try {
      const { start, end } = rangeParams(range);
      await api.generateFunnelReport(start, end);
      const t0 = Date.now();
      const poll = async () => {
        const st = await api.funnelReportStatus();
        if (st.status === "done") {
          setReport(st.report_md);
          setReportLoading(false);
          toast.success("Funnel analysis complete.");
          return;
        }
        if (st.status === "error") {
          setReportLoading(false);
          toast.error(st.error || "Analysis failed.");
          return;
        }
        if (Date.now() - t0 > 180000) {
          setReportLoading(false);
          toast.error("Analysis timed out.");
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setReportLoading(false);
      toast.error(e.response?.data?.detail || "Analysis failed.");
    }
  };

  const pipelines = useMemo(() => data?.pipelines || [], [data]);
  const active = useMemo(
    () => pipelines.find((p) => p.pipeline_id === selectedPipe) || pipelines[0] || null,
    [pipelines, selectedPipe]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (loadError) {
    return <TabLoadError onRetry={load} />;
  }

  const stages = active?.stages || [];
  const maxReached = stages.reduce((m, s) => Math.max(m, s.reached || 0), 0) || 1;
  const subtitle = active
    ? `${active.pipeline_label} · ${fmtNumber(active.deal_count)} deals · ${active.overall_conversion}% overall conversion · ${rangeLabel}`
    : rangeLabel;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-primary" />
          <div>
            <h2 className="font-semibold text-foreground leading-tight">Pipeline</h2>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {pipelines.length > 1 && (
            <Select value={selectedPipe || ""} onValueChange={setSelectedPipe}>
              <SelectTrigger className="w-[190px]" data-testid={TID.funnelPipelineSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pipelines.map((p) => (
                  <SelectItem key={p.pipeline_id} value={p.pipeline_id}>{p.pipeline_label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <CalendarRange className="w-4 h-4 text-muted-foreground" />
          <Select value={range} onValueChange={onRangeChange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid={TID.genFunnelReportBtn} onClick={generate} disabled={reportLoading || !stages.length} variant="outline" size="sm">
            {reportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {report ? "Regenerate" : "AI Analysis"}
          </Button>
        </div>
      </div>

      {!stages.length ? (
        <div className="text-center py-16 text-muted-foreground">
          {data?.note || "No pipeline funnel data available. A funnel requires HubSpot pipeline metadata and deal stage history (not available for CSV uploads)."}
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Stage Conversion Funnel</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stages.map((s, i) => {
                  const width = Math.max((s.reached / maxReached) * 100, 4);
                  return (
                    <div key={s.stage_id}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-foreground">{s.label}</span>
                        <span className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="text-foreground font-medium">{fmtNumber(s.reached)}</span>
                          {s.conversion_from_prev != null && (
                            <span className={s.conversion_from_prev < 60 ? "text-primary" : ""}>
                              {s.conversion_from_prev}% from prev
                            </span>
                          )}
                          {s.avg_days_to_next != null && <span>~{s.avg_days_to_next}d to next</span>}
                        </span>
                      </div>
                      <div className="h-7 bg-muted rounded overflow-hidden">
                        <div
                          className="h-full bg-primary/80 rounded flex items-center justify-end pr-2"
                          style={{ width: `${width}%` }}
                        >
                          {i > 0 && s.dropoff_from_prev != null && s.dropoff_from_prev > 0 && (
                            <span className="text-[10px] text-white/90 font-medium">−{s.dropoff_from_prev}%</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {report && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" /> AI Funnel Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <article data-testid={TID.funnelReportContent} className="prose prose-slate dark:prose-invert max-w-none prose-h2:text-lg prose-strong:text-foreground">
                  <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{report}</ReactMarkdown>
                </article>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <TabReportPrint ref={printRef} title="Pipeline" subtitle={subtitle} reportMd={report}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #0f172a" }}>
              <th style={{ textAlign: "left", padding: 4 }}>Stage</th>
              <th style={{ textAlign: "right", padding: 4 }}>Reached</th>
              <th style={{ textAlign: "right", padding: 4 }}>Conv %</th>
              <th style={{ textAlign: "right", padding: 4 }}>Drop-off %</th>
              <th style={{ textAlign: "right", padding: 4 }}>Avg days→next</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((s) => (
              <tr key={s.stage_id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                <td style={{ padding: 4 }}>{s.label}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{s.reached}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{s.conversion_from_prev ?? "—"}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{s.dropoff_from_prev ?? "—"}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{s.avg_days_to_next ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabReportPrint>
    </div>
  );
}
